import React, { useEffect, useMemo, useState } from 'react';
import { teacherApi } from '../services/api';

// Per-class join code manager opened from the teacher's Sınıflarım grid.
// Same backend as the admin panel's ClassCodesDrawer (just /teacher/classes/...
// instead of /admin/classes/...). Teachers can only mint/manage codes for
// classes they own (homeroom or subject assignment) — backend enforces it
// via assertTeacherOwnsClass.

export interface TeacherClassJoinCode {
  id: string;
  code: string;
  is_active: boolean;
  expires_at: string | null;
  max_uses: number | null;
  use_count: number;
  note: string | null;
  created_at: string;
  created_by: { uuid: string; name: string };
  // Phase 7 — bundled books that get auto-activated when a student redeems
  // this code. Empty array = code grants class access only.
  books?: Array<{ id: number; title: string | null }>;
}

const F = { fontFamily: 'Inter' } as const;

const TeacherClassCodesModal: React.FC<{
  classId: string | number;
  className: string;
  onClose: () => void;
}> = ({ classId, className, onClose }) => {
  const [codes, setCodes] = useState<TeacherClassJoinCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const [maxUses, setMaxUses] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [note, setNote] = useState('');
  // Bundled books that the new code will auto-activate for redeeming students.
  const [allBooks, setAllBooks] = useState<Array<{ id: number; title: string; display_name?: string | null }>>([]);
  const [selectedBookIds, setSelectedBookIds] = useState<number[]>([]);
  const [bookPickerOpen, setBookPickerOpen] = useState(false);

  const reload = () => {
    setLoading(true);
    teacherApi.classCodes(classId)
      .then((r) => setCodes(r.data as TeacherClassJoinCode[]))
      .catch((e: any) => setError(e?.response?.data?.error ?? e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, [classId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Books-for-assignment is already scoped to the teacher's branch +
    // school level + class grade — perfect for the bundle picker.
    teacherApi.booksForAssignment(String(classId))
      .then((r: any) => setAllBooks((r.data || []).map((b: any) => ({ id: b.id, title: b.title, display_name: b.display_name }))))
      .catch(() => setAllBooks([]));
  }, [classId]);

  // Lock body scroll
  useEffect(() => {
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = orig; };
  }, []);

  const onGenerate = async () => {
    setError(null); setCreating(true);
    try {
      await teacherApi.createClassCode(classId, {
        maxUses: maxUses ? Number(maxUses) : null,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        note: note.trim() || null,
        bookIds: selectedBookIds,
      });
      setMaxUses(''); setExpiresAt(''); setNote('');
      setSelectedBookIds([]); setBookPickerOpen(false);
      reload();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e.message);
    } finally {
      setCreating(false);
    }
  };

  const toggleBook = (bookId: number) => {
    setSelectedBookIds((prev) =>
      prev.includes(bookId) ? prev.filter((id) => id !== bookId) : [...prev, bookId]
    );
  };

  // What disabling / deleting a code does to students' books, in words. The count
  // comes from the server (same rule the PATCH/DELETE then applies): only students
  // who joined with THIS code and were given its kitap paketi lose those books —
  // access from a purchase, a yönetici ataması or a kitap kodu is never touched, and
  // nobody is removed from the class.
  const lossNote = (students: number, activations: number) =>
    `Bu kodla sınıfa katılıp kitap paketini alan ${students} öğrencinin paketteki kitaplara erişimi kapanır (${activations} kitap erişimi). ` +
    'Öğrencilerin sınıf kaydı silinmez; satın alma, yönetici ataması veya kitap koduyla edinilen kitaplara dokunulmaz. ' +
    'Kodu tekrar etkinleştirirseniz bu erişim geri verilir.';

  const onToggle = async (c: TeacherClassJoinCode) => {
    setError(null);
    try {
      if (c.is_active) {
        // Disabling: tell the teacher how many students lose their books before it happens.
        const { data } = await teacherApi.classCodeImpact(c.id, false);
        if (data.students > 0 && !window.confirm(`"${c.code}" kodunu devre dışı bırakmak istediğinizden emin misiniz?\n\n${lossNote(data.students, data.activations)}`)) return;
      }
      await teacherApi.updateClassCode(c.id, { isActive: !c.is_active });
      setCodes((prev) => prev.map((p) => (p.id === c.id ? { ...p, is_active: !p.is_active } : p)));
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e.message);
    }
  };

  const onRevoke = async (c: TeacherClassJoinCode) => {
    setError(null);
    try {
      const { data } = await teacherApi.classCodeImpact(c.id, false);
      const loss = data.students > 0 ? `\n\n${lossNote(data.students, data.activations)}` : '';
      if (!window.confirm(`"${c.code}" kodunu silmek istediğinizden emin misiniz? Kod devre dışı kalır, yeni öğrenciler onunla katılamaz; öğrencilerin sınıf kaydı silinmez.${loss}`)) return;
      await teacherApi.revokeClassCode(c.id);
      reload();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e.message);
    }
  };

  const onCopy = async (c: TeacherClassJoinCode) => {
    try {
      await navigator.clipboard.writeText(c.code);
      setCopiedId(c.id);
      setTimeout(() => setCopiedId((id) => (id === c.id ? null : id)), 1500);
    } catch {
      window.prompt('Kodu kopyalayın:', c.code);
    }
  };

  const isUnredeemable = (c: TeacherClassJoinCode) => {
    if (!c.is_active) return true;
    if (c.expires_at && new Date(c.expires_at).getTime() < Date.now()) return true;
    if (c.max_uses != null && c.use_count >= c.max_uses) return true;
    return false;
  };
  const statusLabel = (c: TeacherClassJoinCode) => {
    if (!c.is_active) return 'Devre dışı';
    if (c.expires_at && new Date(c.expires_at).getTime() < Date.now()) return 'Süresi doldu';
    if (c.max_uses != null && c.use_count >= c.max_uses) return 'Tükendi';
    return 'Aktif';
  };

  const sorted = useMemo(
    () => [...codes].sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at)),
    [codes]
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black bg-opacity-40" />
      <div
        className="relative bg-[#f6f6f6] rounded-[24px] w-full max-w-[640px] max-h-[90vh] overflow-y-auto p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-5 right-5 w-[44px] h-[44px] rounded-full flex items-center justify-center hover:bg-gray-200"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round">
            <line x1="4" y1="4" x2="20" y2="20" /><line x1="20" y1="4" x2="4" y2="20" />
          </svg>
        </button>

        <p style={{ ...F, fontSize: 12, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Sınıfa Katılım Kodları
        </p>
        <h2 style={{ ...F, fontSize: 22, fontWeight: 600, color: '#2b2b2b', marginBottom: 18 }}>
          {className}
        </h2>

        <div className="bg-white rounded-[14px] p-5 mb-5" style={{ border: '1px solid #e5e5e5' }}>
          <p style={{ ...F, fontSize: 14, fontWeight: 700, color: '#2b2b2b', marginBottom: 12 }}>Yeni kod oluştur</p>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <label>
              <span style={{ ...F, fontSize: 11, color: '#888', display: 'block', marginBottom: 4 }}>Maks. kullanım</span>
              <input
                type="number" min={1} placeholder="sınırsız"
                value={maxUses} onChange={(e) => setMaxUses(e.target.value)}
                className="w-full bg-[#fafafa] rounded-[10px] px-3 py-2 outline-none text-[14px]"
                style={{ border: '1.5px solid #d8d8d8', ...F }}
              />
            </label>
            <label>
              <span style={{ ...F, fontSize: 11, color: '#888', display: 'block', marginBottom: 4 }}>Geçerlilik bitişi</span>
              <input
                type="datetime-local"
                value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)}
                className="w-full bg-[#fafafa] rounded-[10px] px-3 py-2 outline-none text-[14px]"
                style={{ border: '1.5px solid #d8d8d8', ...F }}
              />
            </label>
          </div>
          <label className="block mb-3">
            <span style={{ ...F, fontSize: 11, color: '#888', display: 'block', marginBottom: 4 }}>Not (opsiyonel)</span>
            <input
              type="text" placeholder="örn. 9-A 2026 dönemi"
              value={note} onChange={(e) => setNote(e.target.value)}
              className="w-full bg-[#fafafa] rounded-[10px] px-3 py-2 outline-none text-[14px]"
              style={{ border: '1.5px solid #d8d8d8', ...F }}
            />
          </label>

          {/* Phase 7: bundled books — every selected book is auto-activated for
              students who redeem this code, regardless of their grade level. */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <span style={{ ...F, fontSize: 11, color: '#888' }}>Kitap paketi (opsiyonel)</span>
              <button
                type="button"
                onClick={() => setBookPickerOpen((v) => !v)}
                className="text-[11px] font-semibold"
                style={{ ...F, color: '#3973bf' }}
              >
                {bookPickerOpen ? 'Gizle' : `${selectedBookIds.length > 0 ? `${selectedBookIds.length} seçili - ` : ''}Kitap seç`}
              </button>
            </div>
            {selectedBookIds.length > 0 && !bookPickerOpen && (
              <div className="flex flex-wrap gap-1">
                {selectedBookIds.map((id) => {
                  const b = allBooks.find((x) => x.id === id);
                  return (
                    <span key={id} className="px-2 py-0.5 rounded-full text-[11px]" style={{ ...F, background: '#eaf1ff', color: '#3973bf' }}>
                      {b?.display_name ?? b?.title ?? `#${id}`}
                    </span>
                  );
                })}
              </div>
            )}
            {bookPickerOpen && (
              <div className="mt-2 max-h-[180px] overflow-y-auto rounded-[10px] bg-white" style={{ border: '1.5px solid #d8d8d8' }}>
                {allBooks.length === 0 ? (
                  <p className="text-center text-gray-400 text-[12px] py-4">Yetkinizdeki kitap bulunamadı.</p>
                ) : (
                  allBooks.map((b) => (
                    <label key={b.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-[#fafafa] border-b border-gray-100 last:border-b-0">
                      <input type="checkbox" checked={selectedBookIds.includes(b.id)} onChange={() => toggleBook(b.id)} />
                      <span className="text-[12px]" style={{ ...F }}>{b.display_name ?? b.title}</span>
                    </label>
                  ))
                )}
              </div>
            )}
          </div>

          <button
            disabled={creating}
            onClick={onGenerate}
            className="w-full rounded-[12px] py-2.5 hover:opacity-90 disabled:opacity-50"
            style={{ background: '#e5599b' }}
          >
            <span style={{ ...F, fontSize: 14, fontWeight: 700, color: '#fff' }}>
              {creating
                ? 'Oluşturuluyor…'
                : selectedBookIds.length > 0
                  ? `Kod Oluştur (${selectedBookIds.length} kitap paketli)`
                  : 'Kod Oluştur'}
            </span>
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-[10px] px-4 py-2 text-[13px] text-[#c73d74]" style={{ background: '#fdecef' }}>{error}</div>
        )}

        <div>
          {loading ? (
            <p className="text-center text-gray-400 py-6" style={F}>Yükleniyor…</p>
          ) : sorted.length === 0 ? (
            <p className="text-center text-gray-400 py-6" style={F}>Bu sınıf için henüz kod yok.</p>
          ) : (
            <ul className="space-y-3">
              {sorted.map((c) => {
                const dim = isUnredeemable(c);
                return (
                  <li key={c.id}
                    className="bg-white rounded-[12px] p-4"
                    style={{ border: '1px solid #e5e5e5', opacity: dim ? 0.6 : 1 }}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <button onClick={() => onCopy(c)}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-[8px] hover:opacity-80"
                        style={{ background: '#f6f6f6', fontFamily: 'monospace', fontSize: 16, fontWeight: 600, color: '#3973bf' }}>
                        {c.code}
                        <span style={{ fontSize: 11, color: '#888' }}>{copiedId === c.id ? '✓ kopyalandı' : '📋'}</span>
                      </button>
                      <span
                        className="px-2 py-0.5 rounded-full text-[11px] font-semibold"
                        style={{
                          background: dim ? '#e5e5e5' : '#dff5e0',
                          color: dim ? '#888' : '#2a8a3f',
                          ...F,
                        }}>
                        {statusLabel(c)}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[12px]" style={{ ...F, color: '#555' }}>
                      <span>Kullanım: {c.use_count}{c.max_uses != null ? ` / ${c.max_uses}` : ' / ∞'}</span>
                      <span>{c.expires_at ? `Bitiş: ${new Date(c.expires_at).toLocaleString('tr-TR')}` : 'Süresiz'}</span>
                      <span className="col-span-2 text-[11px] text-gray-400">
                        {new Date(c.created_at).toLocaleString('tr-TR')} - {c.created_by.name}
                      </span>
                      {c.note && <span className="col-span-2 italic">"{c.note}"</span>}
                      {c.books && c.books.length > 0 && (
                        <div className="col-span-2 mt-1 flex flex-wrap gap-1">
                          {c.books.map((b) => (
                            <span key={b.id} className="px-2 py-0.5 rounded-full text-[10px]" style={{ background: '#eaf1ff', color: '#3973bf' }}>
                              📚 {b.title ?? `#${b.id}`}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => onToggle(c)}
                        className="text-[12px] px-3 py-1 rounded-[8px] hover:bg-gray-100" style={F}>
                        {c.is_active ? 'Devre dışı bırak' : 'Tekrar etkinleştir'}
                      </button>
                      {c.is_active && (
                        <button onClick={() => onRevoke(c)}
                          className="text-[12px] px-3 py-1 rounded-[8px] hover:bg-red-50" style={{ ...F, color: '#c73d74' }}>
                          Sil
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeacherClassCodesModal;
